import * as React from 'react';
import classNames from 'classnames';
import { TableScroll } from 'modules/types/commons/table';
import { Checkbox } from '@beehive/components';
// @ts-ignore
import _ from 'lodash';
import { isBlank } from 'utils/object';
import { getTableCellClassName, calcScrollYaxis, getScrollXAxis } from 'utils/table';
import { FILTER_OPTIONS } from 'utils/filter';
import { COLUMNS, localeColumnsAttr } from './utils';
import PropTypes from 'prop-types';
import { tableColumnWidthToRem } from '@mpc/common/src/';
import { apiVersionCheck } from '@mpc/common/src/utils/versionCheck';

const INIT_TABLE_OPTIONS = {
  scroll: { x: true },
  scrollMod: 'innerWindow',
  tablePrefix: 'beehive-table',
  tableElementSelector: '.workbench-list .beehive-table',
};

const INIT_FILTER_OPTIONS = {
  filterKeys: ['wb_products', 'plan_users'],
};

/** 列表搜索项key => 列表搜索项下拉获取接口的key */
const filterOptionKeysMap: { [key: string]: string } = {
  'plan_users': apiVersionCheck() ? 'wb_plan_users' : 'plan_users',
}

const getFilterOptionKeys = (filterKeys: string[]) => {
  return filterKeys?.map?.(key => filterOptionKeysMap[key] || key);
}

const INIT_PAGINATION_OPTIONS = {};

/**
 * innerWindow: 一屏内滚动
 * innerPage: 网页内滚动，表格不滚动
 */
export type ScrollMod = 'innerWindow' | 'innerPage';

export interface TableOptionsParam {
  getColumns?: Function;
  scrollMod?: ScrollMod;
  tablePrefix?: string;
  wrappedBottomDistance?: number;
  scroll?: TableScroll;
  menuKey?: string;
}

export type TableOptions = TableOptionsParam | Function | null;

export interface FilterOptions {
  filterKeys?: string[];
}

// tslint:disable-next-line: no-empty-interface
export interface PaginationOptions {

}

function shallowMerge(src: any, dest: any) {
  return { ...src, ...dest };
}

const listAdvanced = (
  tableOptions?: TableOptions,
  filterOptions?: FilterOptions,
  paginationOptions?: PaginationOptions,
) => {
  let initTableOptions = (typeof (tableOptions) !== 'function') && shallowMerge(INIT_TABLE_OPTIONS, tableOptions);
  let initFilterOptions = shallowMerge(INIT_FILTER_OPTIONS, filterOptions);
  const initPaginationOptions = shallowMerge(INIT_PAGINATION_OPTIONS, paginationOptions);

  return (WrappedComponent: any) => (
    class extends React.Component<any, any> {

      public static contextTypes = {
        intl: PropTypes.object,
      };

      constructor(props: any, context: any) {
        super(props);
        const { intl } = context;
        if (typeof tableOptions === 'function') {
          initTableOptions = shallowMerge(INIT_TABLE_OPTIONS, tableOptions({ ...props, intl }));
        }
        if ('filterOptions' in props) {
          initFilterOptions = shallowMerge(INIT_FILTER_OPTIONS, props.filterOptions);
        }
        this.state = {
          searchValue: '',
          // 初始化true，为了拿到准确的tabebody的高度
          tableBodyHeight: true,
          tableBodyWidth: 0,
        };
        this.handleWindowResize = _.debounce(this.handleWindowResize, 100);
      }

      public componentDidMount() {
        const { menuKey, getFilterOptions } = this.props;
        const { filterKeys } = initFilterOptions;

        window.addEventListener('resize', this.handleWindowResize);

        getFilterOptions(menuKey, getFilterOptionKeys(filterKeys));
        this.getList();
      }

      public componentDidUpdate() {
        this.setScrollXaxis();
        this.setScrollYaxis();
      }

      componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
      }

      handleWindowResize = () => {
        this.setScrollXaxis();
        this.setScrollYaxis();
      }

      public handlePageChange = (currentPage: number, pageSize: number, isPageSizeChange: boolean) => {
        this.getList(this.props.data.conditions, {
          current_page: isPageSizeChange ? 1 : currentPage,
          page_size: pageSize,
        });
      }

      public handleConditonChange = (key: string, condition: any, isSelectedAll: boolean) => {
        const { searchValue } = this.state;
        if (isSelectedAll) {
          condition = undefined;
        }
        this.getList({
          [key]: condition,
          search_text: searchValue,
        }, { current_page: 1 });
      }

      public handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
          searchValue: event.target.value,
        });
      }

      public handleSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { searchValue } = this.state;
        if (e.keyCode === 13) {
          this.getList({ search_text: searchValue }, { current_page: 1 });
        } else {
          const { data: { conditions } } = this.props;
          this.props.changeConditions({ ...conditions, search_text: searchValue });
        }
      }

      public handleSearch = (options = {}) => {
        options = { search_text: this.state.searchValue, ...options };
        this.getList({ ...options }, { current_page: 1 });
      }

      public getColumnHeaderCell = () => {
        const tableData = _.get(this.props, 'data.list.res') || [];
        return {
          name: (record: any) => {
            return {
              className: classNames({
                'tree-column-padding': tableData.some((i: any) => i.children),
              }),
            };
          },
        };
      }

      public getFilterProps = () => {
        const { intl } = this.context;
        const { searchValue } = this.state;
        const filterOptions = _.get(this.props, 'data.filterOptions', {});
        const conditions = _.get(this.props, 'data.conditions', {});
        const { filterKeys } = initFilterOptions;

        const filters = filterKeys.filter((key: string) => !!FILTER_OPTIONS[key]).map((key: string) => {
          const currFilterOptions = filterOptions[filterOptionKeysMap[key] || key];
          return {
            label: currFilterOptions?.name,
            key,
            ...FILTER_OPTIONS[key],
            options: currFilterOptions?.options || [],
            onChange: (value: any, isSelectedAll: boolean) => this.handleConditonChange(key, value, isSelectedAll),
          };
        });
        const serachProps = {
          // placeholder: intl.formatMessage(messages.placeholder),
          value: searchValue,
          onChange: this.handleSearchChange,
          onKeyUp: this.handleSearchKeyUp,
          onSearch: this.handleSearch,
          onClear: () => this.handleSearch({ search_text: '' }),
        };

        return { canSearch: true, filters, conditions, serachProps };
      }

      public getTableProps = () => {
        const { scroll } = initTableOptions;
        const columns = this.getColumns();
        const list = _.get(this.props, 'data.list') || {};

        return shallowMerge({
          ...initTableOptions,
          data: list.res,
          columns,
          scroll: { ...scroll, y: this.state.tableBodyHeight },
          expandIconColumnIndex: columns.findIndex((col: any) => col.key === 'name'),
          needIndentSpaced: true,
          rowKey: (record: any) => record.id,
        }, typeof tableOptions === 'function' ? tableOptions(this.props) : tableOptions);
      }

      public getList = (condition?: any, pagination = {}) => {
        const { data = {}, getList, changeConditions, menuKey } = this.props;
        const { conditions, list = { current_page: 1, page_size: 15 } } = data;
        changeConditions({ ...conditions, ...condition });
        getList(
          menuKey,
          { ...conditions, ...condition },
          Object.assign({
            current_page: list.current_page,
            page_size: list.page_size,
          }, pagination),
        );
      }

      public getDefaultColumnRender = (v: any, r: any, i: number) => {
        return isBlank(v) ? '--' : v;
      }

      public getColumnsTitle = () => {
        const { menuKey, changeCheck } = this.props;
        const checkedState = _.get(this.props, 'data.list.checkedState') || {};
        const { checkedAll = false, checkedSome = false } = checkedState;
        return {
          checkbox: (
            <Checkbox
              checked={checkedAll}
              indeterminate={checkedSome}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                changeCheck && changeCheck(menuKey, {
                  isCheckAll: true,
                  checkedAll: e.target.checked,
                });
              }}
            />
          ),
        };
      }

      public getColumnsRender = () => {
        const { menuKey, changeCheck } = this.props;
        return {
          checkbox: (v: any, r: any, i: number) => {
            return (
              <Checkbox
                checked={r.checked_by_user}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  changeCheck && changeCheck(menuKey, { row: r, checked: e.target.checked });
                }}
              />
            );
          },
        };
      }

      public getColumns = () => {
        const { intl } = this.context;
        const { tableBodyWidth } = this.state;
        const { getColumns } = initTableOptions;
        const columnsMapping = getColumns && getColumns({
          props: this.props,
        }) || {};
        const columnsDefaultRender = this.getDefaultColumnRender;
        const columnsRender = this.getColumnsRender();
        const columnsTitle = this.getColumnsTitle();
        const columnsHeaderCell = this.getColumnHeaderCell();
        const columns = _.get(this.props, 'data.list.columns') || COLUMNS;
        let tableColumnsWidth = 0;
        let hasUndefinedWidth = false;

        const tableColumns = columns.map((column: any) => {
          const { key, name } = column;
          const aColumn: any = {
            key,
            dataIndex: key,
            className: getTableCellClassName(key),
            render: columnsRender[key] || columnsDefaultRender,
            title: columnsTitle[key] || name,
            width: 110,
            onHeaderCell: columnsHeaderCell[key],
            ...columnsMapping[key],
            ..._.get(localeColumnsAttr, `[${intl.locale}][${key}]`),
          };

          if (aColumn.width) {
            tableColumnsWidth += aColumn.width;
          } else {
            hasUndefinedWidth = true;
          }

          return aColumn;
        });

        if (tableBodyWidth && !hasUndefinedWidth && tableColumnsWidth && tableColumnsWidth <= tableBodyWidth) {
          tableColumns.forEach((column: any) => {
            if (column.fixed) {
              column.fixed = undefined;
            }
          });
        }

        return tableColumns.map(tableColumnWidthToRem);
      }

      setScrollXaxis = () => {
        const { tableBodyWidth } = this.state;
        const xaxis = getScrollXAxis(initTableOptions);
        if (xaxis && xaxis !== tableBodyWidth) {
          this.setState({ tableBodyWidth: xaxis });
        }
      }

      public setScrollYaxis = () => {
        const { tableBodyHeight } = this.state;
        const yaxis = calcScrollYaxis(initTableOptions);

        if (yaxis && (typeof this.state.tableBodyHeight !== 'number' || yaxis !== tableBodyHeight)) {
          this.setState({ tableBodyHeight: yaxis });
        }
      }

      public render() {
        const list = _.get(this.props, 'data.list') || {};
        const loading = _.get(this.props, 'data.loading');

        const filterProps = shallowMerge(initFilterOptions, this.getFilterProps());
        const tableProps = this.getTableProps();

        const paginationProps = shallowMerge(initPaginationOptions, {
          total: list.total_count,
          current: list.current_page,
          pageSize: list.page_size,
          onChange: this.handlePageChange,
        });
        const blockWithLoadingProps = { loading };

        return (
          <WrappedComponent
            {...this.props}
            hasFilter
            filterProps={filterProps}
            tableProps={tableProps}
            paginationProps={paginationProps}
            blockWithLoadingProps={blockWithLoadingProps}
          />
        );
      }
    }
  );
};

export default listAdvanced;
